import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";

export default function CategorySection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <section id="categories" className="mx-auto max-w-[1280px] px-4 md:px-6 py-16 md:py-20">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div className="max-w-2xl">
          <h2 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950">
            {t("categories.title")}
          </h2>
          <p className="mt-3 text-foreground-600 leading-relaxed">
            {t("categories.subtitle")}
          </p>
        </div>

        <div className="relative w-full md:w-72" ref={ref}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex w-full items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm font-medium text-foreground-800 hover:border-background-300 cursor-pointer"
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span className="flex items-center gap-2 whitespace-nowrap">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-layout-grid-line text-primary-600" />
              </span>
              {t("categories.label")}
            </span>
            <span className="w-5 h-5 flex items-center justify-center">
              <i
                className={`ri-arrow-down-s-line transition-transform ${
                  open ? "rotate-180" : ""
                }`}
              />
            </span>
          </button>

          {open && (
            <div className="absolute z-30 mt-2 w-full max-h-96 overflow-y-auto rounded-lg border border-background-200 bg-background-50 py-1">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.href}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 whitespace-nowrap cursor-pointer"
                >
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 flex items-center justify-center text-primary-600">
                      <i className={cat.icon} />
                    </span>
                    {t(cat.nameKey)}
                  </span>
                  <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                    <i className="ri-arrow-right-up-line" />
                  </span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
        {categories.map((cat) => (
          <a
            key={cat.id}
            href={cat.href}
            target="_blank"
            rel="noopener nofollow"
            className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 hover:border-background-300 cursor-pointer"
          >
            <div className="relative w-full h-44 overflow-hidden">
              <img
                src={cat.image}
                alt={`${t(cat.nameKey)} — AllChinaBuy Spreadsheet`}
                title={`${t(cat.nameKey)} AllChinaBuy Spreadsheet`}
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <span className="flex items-center gap-2 text-sm font-medium text-foreground-900">
                <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                  <i className={cat.icon} />
                </span>
                <span className="whitespace-nowrap">{t(cat.nameKey)}</span>
              </span>
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400 group-hover:text-primary-600 transition-colors">
                <i className="ri-arrow-right-up-line" />
              </span>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}